import { createReducer, on, Action } from '@ngrx/store';

import * as WagerActions from './wager.actions';
import { Dictionary, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import * as _ from "lodash";
import { IEventDetail, IGetEventsDetailsResponse, IRace, sortEvents } from '@amwager/util-ts';
import { IEventTrackRaceState, ISelectedRaceRaceResult } from './wager.models';



export const WAGER_FEATURE_KEY = 'wager';
// export interface WagerPartialState {
//   readonly [WAGER_FEATURE_KEY]: IWagerState;
// }



/**
 * Event Track Race
 */
export const EVENT_TRACK_RACE_FEATURE_KEY = 'eventTrackRace';

export const eventTrackRaceAdapter: EntityAdapter<IGetEventsDetailsResponse> =
  createEntityAdapter<IGetEventsDetailsResponse>();

const stateGuard = (state: IEventTrackRaceState): IEventTrackRaceState => state;
const stateTypeGuard = <T>(state: T): T => state;

const initialEventTrackRaceState: IEventTrackRaceState = eventTrackRaceAdapter.getInitialState(
  stateGuard({
    id: 'event-track-race',
    name: 'event-track-race',
    selectedId: undefined,
    error: undefined,
    isLoaded: false,
    isRequestingAPI: undefined,
    ids: [] as string[] | number[],
    entities: {} as Dictionary<IGetEventsDetailsResponse>,
    selectedEventTrack: undefined,
    selectedRace: undefined,
    currentTicket: {
      payload: undefined
    },
  })
);


const eventTrackRaceReducerFN = createReducer(
  initialEventTrackRaceState,
  on(WagerActions.clearWagerState, (state) => stateGuard({
    ...initialEventTrackRaceState,
    ids: state?.ids,
    entities: state?.entities,
    selectedEventTrack: state?.selectedEventTrack,
    selectedRace: state?.selectedRace
  })),

  /**
   * GET API Request
   */
  on(WagerActions.getEventsDetailsAPI, (state) => stateGuard({
    ...state,
    ...{
      ...initialEventTrackRaceState,
      ids: state?.ids || [],
      entities: state?.entities || {},
      selectedEventTrack: state?.selectedEventTrack || undefined,
      selectedRace: state?.selectedRace || undefined
    },
    isRequestingAPI: true,
  })),

  // bet section
  on(WagerActions.getCountersAPI, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    getCounters: {
      ...(state as IEventTrackRaceState).getCounters,
      isProcessing: true,
      isSuccess: undefined,
      response: undefined
    },
    error: undefined
  })),

  on(WagerActions.getBetsAPI, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    getBets: {
      ...(state as IEventTrackRaceState).getBets,
      isProcessing: true,
      isSuccess: undefined,
      response: undefined
    },
    error: undefined
  })),

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  on(WagerActions.submitSavedBetAPI, (state, action) => stateGuard({
    ...state as IEventTrackRaceState,
    getBets: {
      ...(state as IEventTrackRaceState).getBets,
      // myBets: state?.getBets?.myBets?.map((bet) =>
      //   bet.TicketId === action.ticket.TicketId ? action.ticket : bet),
      isProcessing: true
    },
    error: undefined
  })),

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  on(WagerActions.removeSavedBetAPI, (state, action) => stateGuard({
    ...state as IEventTrackRaceState,
    getBets: {
      ...(state as IEventTrackRaceState).getBets,
      // myBets: state?.getBets?.myBets?.map((bet) =>
      //   bet.TicketId === action.ticket.TicketId ? action.ticket : bet),
      isProcessing: true
    },
    error: undefined
  })),

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  on(WagerActions.cancelBetAPI, (state, action) => stateGuard({
    ...state as IEventTrackRaceState,
    getBets: {
      ...(state as IEventTrackRaceState).getBets,
      // myBets: state?.getBets?.myBets?.map((bet) =>
      //   bet.TicketId === action.ticket.TicketId ? action.ticket : bet),
      isProcessing: true, // default
      isSuccess: undefined, // default
      response: undefined // default
    },
    error: undefined
  })),

  on(WagerActions.saveBetAPI, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    saveBet: {
      ...(state as IEventTrackRaceState).saveBet,
      isProcessing: true,
      isSuccess: undefined,
      response: undefined
    },
    error: undefined
  })),

  on(WagerActions.betNowAPI, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    addBetAmount: {
      ...(state as IEventTrackRaceState).addBetAmount,
      isProcessing: true,
      isSuccess: undefined,
      response: undefined
    },
    error: undefined
  })),

  on(WagerActions.setIsShowBetPreview, (state, { isShowBetPreview }) => stateGuard({
    ...state as IEventTrackRaceState,
    betPreview: isShowBetPreview
  })),

  on(WagerActions.getPoolMatrixAPI, (state, { requests, fromPusher }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRaceMatrix: {
      ...(state as IEventTrackRaceState).selectedRaceMatrix,
      [requests?.poolId]: {
        isProcessing: true,
        isSuccess: undefined,
        response: (() => {
          if (fromPusher) {
            return (state as IEventTrackRaceState).selectedRaceMatrix?.[requests?.poolId]?.response
          }

          return undefined;
        })()
      }
    },
    error: undefined
  })),

  on(WagerActions.getPoolsTotalsAPI, (state, { fromPusher }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRacePoolsTotals: {
      ...(state as IEventTrackRaceState).selectedRacePoolsTotals,
      isProcessing: true,
      isSuccess: undefined,
      response: (() => {
        if (fromPusher) {
          return (state as IEventTrackRaceState).selectedRacePoolsTotals?.response
        }

        return undefined;
      })()
    },
    error: undefined
  })),

  on(WagerActions.getWillPaysAPI, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRaceWillPays: {
      ...(state as IEventTrackRaceState).selectedRaceWillPays,
      isProcessing: true,
      isSuccess: undefined,
      response: undefined
    },
    error: undefined
  })),

  on(WagerActions.getRaceAllOddsMatrixesAPI, (state, { pool, fromPusher }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedWINRaceAllOddsMatrixes: {
      ...(state as IEventTrackRaceState).selectedWINRaceAllOddsMatrixes,
      [pool]: {
        ...(state as IEventTrackRaceState).selectedWINRaceAllOddsMatrixes?.[pool],
        isProcessing: true,
        isSuccess: undefined,
        response: (() => {
          if (fromPusher) {
            return (state as IEventTrackRaceState).selectedWINRaceAllOddsMatrixes?.[pool]?.response
          }

          return undefined;
        })()
      },
    },
    error: undefined
  })),

  // on(WagerActions.getEventRaceVideoAPI, (state) => stateGuard({
  //   ...state as IEventTrackRaceState,
  // })),

  on(WagerActions.getAllEventsNameAndIdAPI, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    allEventsNameAndId: {
      ...(state as IEventTrackRaceState).allEventsNameAndId,
      isProcessing: true,
      isSuccess: undefined,
      response: undefined
    },
    error: undefined
  })),

  on(WagerActions.getReplaysAPI, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    replays: {
      ...(state as IEventTrackRaceState).replays,
      isProcessing: true,
      isSuccess: undefined,
      response: undefined
    },
    error: undefined
  })),

  on(WagerActions.getRunnersAPI, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    runners: undefined
  })),

  on(WagerActions.getEventRaceVideoReplayAPI, (state) => stateGuard({
    ...state,
    selectedRace: {
      ...state?.selectedRace,
      raceResults: {
        ...state?.selectedRace?.raceResults,
        replayVideo: undefined,
        errorMsg: undefined
      },
      action: 'getEventRaceVideoReplayAPI'
    } as IRace
  })),

  on(WagerActions.getRaceResultAPI, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRace: {
      ...state?.selectedRace,
      raceResults: {
        ...state?.selectedRace?.raceResults,
        response: undefined,
        isGettingResult: true,
        isSuccessResult: undefined,
      },
      action: 'getRaceResultAPI'
    } as IRace
  })),



  /**
   * GET API Response SUCCESS
   */
  on(WagerActions.getEventsDetailsAPISuccess, (state, { successResponse }) => stateGuard({
    ...eventTrackRaceAdapter.setAll(
      successResponse
        // TODO: change events to successResponse
        ? sortEvents(
            _.map(successResponse, (eventTrackRace: IGetEventsDetailsResponse) => {
              // console.log('reducer:eventTrackRace:', eventTrackRace)
              return {
                id: eventTrackRace?.ToteResponse?.EventDetail?.EventId, // set an id
                // ...eventTrackRace
                ...{
                  ...eventTrackRace, // event from list
                  ToteResponse: {
                    ...eventTrackRace?.ToteResponse,
                    EventDetail: stateTypeGuard<IEventDetail>({
                      ...eventTrackRace?.ToteResponse?.EventDetail,
                      Races: _.map(eventTrackRace?.ToteResponse?.EventDetail?.Races, (race) => {
                        return {
                          ...race,
                          runId: eventTrackRace?.ToteResponse?.EventDetail?.RunId,
                          eventId: eventTrackRace?.ToteResponse?.EventDetail?.EventId
                        }
                      }),
                      currentRace: (() => {
                        // add currentRace for this event
                        return {
                          ..._.find(eventTrackRace?.ToteResponse?.EventDetail?.Races || [], 'Current') as IRace,
                          runId: eventTrackRace?.ToteResponse?.EventDetail?.RunId,
                          eventId: eventTrackRace?.ToteResponse?.EventDetail?.EventId
                        }
                      })()
                    })
                  },
                  eventName: eventTrackRace?.ToteResponse?.EventDetail?.EventName
                }
              }
            }
            ),
            'MTP',
            ['Thoroughbred','Quarterhorse','Greyhound','Harness','International']
          )
        : []
      ,
      {
        ...state,
        isRequestingAPI: false,
        isLoaded: true,
      }
    )
  })),

  on(WagerActions.getEventDetailAPISuccess, (state, { eventId, successResponse, raceId }) => stateGuard({
    ...state,
    entities: (() => {
      // console.log('reducer:getEventDetailAPISuccess:data:,', successResponse )
      if (_.isEmpty(successResponse)) return state?.entities;

      const id = eventId;
      const runId = successResponse?.ToteResponse?.EventDetail?.RunId;

      if (runId !== state?.entities[id]?.ToteResponse?.RunId) return state?.entities;

      return state?.entities[id]
       ?  {
            ...state?.entities,
            [id]: {
              ...state?.entities[id],
              ToteResponse: {
                ...state?.entities[id]?.ToteResponse,
                ...successResponse?.ToteResponse,
                EventDetail: stateTypeGuard<IEventDetail>({
                  ...state?.entities[id]?.ToteResponse?.EventDetail,
                  ...successResponse?.ToteResponse?.EventDetail,
                  Races: _.map(successResponse?.ToteResponse?.EventDetail?.Races, (race) => {
                    return {
                      ...race,
                      runId: successResponse?.ToteResponse?.EventDetail?.RunId,
                      eventId: successResponse?.ToteResponse?.EventDetail?.EventId
                    }
                  }),
                  currentRace: (() => {
                    // add currentRace for this event
                    return {
                      // ..._.find(successResponse?.ToteResponse?.EventDetail?.Races || [], 'Current'),
                      ...(() => {
                        return raceId
                          ? (() => {
                              return {
                                ..._.find(successResponse?.ToteResponse?.EventDetail?.Races || [], ['RaceId', raceId+1]),
                                Current: true
                              } || _.find(successResponse?.ToteResponse?.EventDetail?.Races || [], 'Current')
                            })()
                          : _.find(successResponse?.ToteResponse?.EventDetail?.Races || [], 'Current')
                      })(),
                      runId,
                      eventId
                    }
                  })()
                }),
                // RunId: runId
              },
              // eventName: state?.entities[id]?.ToteResponse?.EventDetail?.EventName
            }
          } as unknown as Dictionary<IGetEventsDetailsResponse>
       : { ...state?.entities }
    })(),
    selectedEventTrack: (() => {
      if (!successResponse) return state?.selectedEventTrack;

      const id = eventId;
      const runId = successResponse?.ToteResponse?.EventDetail?.RunId;

      if (runId !== state?.selectedEventTrack?.ToteResponse?.RunId) return state?.selectedEventTrack;

      if (id === state?.selectedEventTrack?.id) {
        return  {
          ...state?.selectedEventTrack,
          ToteResponse: {
            ...state?.entities[id]?.ToteResponse,
            ...successResponse?.ToteResponse,
            EventDetail: stateTypeGuard<IEventDetail>({
              ...state?.entities[id]?.ToteResponse?.EventDetail,
              ...successResponse?.ToteResponse?.EventDetail,
              Races: _.map(successResponse?.ToteResponse?.EventDetail?.Races, (race) => {
                return {
                  ...race,
                  runId: successResponse?.ToteResponse?.EventDetail?.RunId,
                  eventId: successResponse?.ToteResponse?.EventDetail?.EventId
                }
              }),
              currentRace: (() => {
                // add currentRace for this event
                return {
                  // ..._.find(successResponse?.ToteResponse?.EventDetail?.Races || [], 'Current'),
                  ...(() => {
                    return raceId
                      ? (() => {
                          return {
                            ..._.find(successResponse?.ToteResponse?.EventDetail?.Races || [], ['RaceId', raceId+1]),
                            Current: true
                          } || _.find(successResponse?.ToteResponse?.EventDetail?.Races || [], 'Current')
                        })()
                      : _.find(successResponse?.ToteResponse?.EventDetail?.Races || [], 'Current')
                  })(),
                  runId,
                  eventId
                }
              })()
            }),
            // RunId: runId
          },
          // eventName: successResponse?.ToteResponse?.EventDetail?.EventName
        }
      }

      return state?.selectedEventTrack
    })(),
    selectedRace: (() => {
      if (!successResponse) return state?.selectedRace;

      const id = eventId;
      const runId = successResponse?.ToteResponse?.EventDetail?.RunId;

      if (runId !== state?.selectedRace?.runId) return state?.selectedRace;
      if (id === state?.selectedRace?.eventId) return {
        ...{
          ...(() => {
            return raceId
              ? (() => {
                  return {
                    ..._.find(successResponse?.ToteResponse?.EventDetail?.Races || [], ['RaceId', raceId+1]),
                    Current: true
                  } || _.find(successResponse?.ToteResponse?.EventDetail?.Races || [], 'Current')
                })()
              : _.find(successResponse?.ToteResponse?.EventDetail?.Races || [], 'Current')
          })(),
          action: 'getEventDetailAPISuccess'
        },
        runId,
        eventId
      }

      return state?.selectedRace
    })(),
  })),

  on(WagerActions.getRaceResultAPISuccess, (state, { successResponse, eventId }) => stateGuard({
    ...state,
    selectedRace: {
      ...state?.selectedRace,
      raceResults: (() => {
        if (
          // state?.selectedRace?.runId === successResponse?.ToteResponse?.RunId &&
          state?.selectedRace?.eventId === successResponse?.ToteResponse?.EventId
        ) {
          return {
            ...successResponse,
            totalHandle: (() => {
              return _.reduce(
                _.map( // select only Handle data
                  _.filter( // remove undefined or null
                    _.map( // get pool only from PoolPrices
                      successResponse?.ToteResponse?.PoolPrices,
                      (price) =>
                        _.find(
                          successResponse?.PoolsTotal,
                          (pool) => (price?.PoolId === pool?.Id) ? pool : undefined // return data from PoolPrices
                        )
                    ),
                    (filterData) => filterData
                  ),
                  (pool) => pool?.Handle
                ),
                (sum, n) => sum + n,
                0
              )
            })(),
            replayVideo: (() => {
              if (
                state?.selectedRace?.eventId === successResponse?.ToteResponse?.EventId
                && state?.selectedRace?.RaceId === successResponse?.ToteResponse?.RaceId
              ) {
                return state?.selectedRace?.raceResults?.replayVideo
              }
              else return undefined
            })(),
            response: successResponse?.ToteResponse || undefined,
            isGettingResult: false,
            isSuccessResult: true,
          }
        }
        else return {
          response: undefined,
          isGettingResult: false,
          isSuccessResult: true,
        }
      })(),
      eventId,
      action: 'getRaceResultAPISuccess'
    } as IRace
  })),

  // bet section
  on(WagerActions.getCountersAPISuccess, (state, { successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    getCounters: {
      ...(state as IEventTrackRaceState).getCounters,
      isProcessing: false,
      isSuccess: true,
      payload: successResponse
    }
  })),

  on(WagerActions.getBetsAPISuccess, (state, { successResponse, status }) => stateGuard({
    ...state as IEventTrackRaceState,
    getBets: {
      ...(state as IEventTrackRaceState).getBets,
      isProcessing: false, // default
      isSuccess: true, // default
      response: successResponse, // default
      payload: successResponse,
      [status]: successResponse
    }
  })),

  on(WagerActions.submitSavedBetAPISuccess, (state, { ticket, successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    getBets: {
      ...(state as IEventTrackRaceState).getBets,
      payload: state?.getBets?.payload?.filter((bet) => bet.TicketId !== ticket.TicketId),
      response: successResponse,
      isSuccess: true,
      saved: state?.getBets?.saved?.filter((bet) => bet.TicketId !== ticket.TicketId),
    },
  })),

  on(WagerActions.removeSavedBetAPISuccess, (state, { ticket, successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    getBets: {
      ...(state as IEventTrackRaceState).getBets,
      payload: state?.getBets?.payload?.filter((bet) => bet.TicketId !== ticket.TicketId),
      response: successResponse,
      isSuccess: true,
      saved: state?.getBets?.saved?.filter((bet) => bet.TicketId !== ticket.TicketId),
    },
  })),

  on(WagerActions.cancelBetAPISuccess, (state, { ticket, successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    getBets: {
      ...(state as IEventTrackRaceState).getBets,
      payload: state?.getBets?.payload?.map((bet) =>
        bet.TicketId === ticket.TicketId ? ticket : bet),
      response: successResponse,
      isSuccess: true,
      active: state?.getBets?.active?.map((bet) =>
        bet.TicketId === ticket.TicketId ? ticket : bet),
    },
  })),

  on(WagerActions.saveBetAPISuccess, (state, { successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    saveBet: {
      ...(state as IEventTrackRaceState).saveBet,
      isProcessing: false,
      isSuccess: true,
      response: successResponse,
    }
  })),

  on(WagerActions.betNowAPISuccess, (state, { successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    addBetAmount: {
      ...(state as IEventTrackRaceState).addBetAmount,
      isProcessing: false,
      isSuccess: true,
      response: successResponse,
    }
  })),

  // matrix
  on(WagerActions.getPoolMatrixAPISuccess, (state, { successResponse, poolId, eventId, raceId }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRaceMatrix: (() => {
      if (successResponse?.ToteResponse?.PoolId) {
        if (
          (state as IEventTrackRaceState)?.selectedRace?.eventId === eventId
          && (state as IEventTrackRaceState)?.selectedRace?.RaceId === raceId
        ) {
          return {
            ...(state as IEventTrackRaceState).selectedRaceMatrix,
            [successResponse?.ToteResponse?.PoolId]: {
              isProcessing: false,
              isSuccess: true,
              response: successResponse
            }
          }
        }
        else return {
          ...(state as IEventTrackRaceState).selectedRaceMatrix,
          [poolId]: undefined
        }
      }
      else return {
        ...(state as IEventTrackRaceState).selectedRaceMatrix,
        [poolId]: undefined
      }
    })()
  })),

  on(WagerActions.getPoolsTotalsAPISuccess, (state, { successResponse, raceId, eventId }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRacePoolsTotals: {
      ...(state as IEventTrackRaceState).selectedRacePoolsTotals,
      isProcessing: false,
      isSuccess: true,
      response: (() => {
        if (
          (state as IEventTrackRaceState)?.selectedRace?.eventId === eventId
          && (state as IEventTrackRaceState)?.selectedRace?.RaceId === raceId
        ) {
          return successResponse
        }
        else {
          return undefined
        }
      })()
    }
  })),

  on(WagerActions.getWillPaysAPISuccess, (state, { successResponse, raceId, eventId }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRaceWillPays: {
      ...(state as IEventTrackRaceState).selectedRaceWillPays,
      isProcessing: false,
      isSuccess: true,
      response: (() => {
        if (
          (state as IEventTrackRaceState)?.selectedRace?.eventId === eventId
          && (state as IEventTrackRaceState)?.selectedRace?.RaceId === raceId
        ) {
          return successResponse
        }
        else {
          return undefined
        }
      })()
    }
  })),

  on(WagerActions.getRaceAllOddsMatrixesAPISuccess, (state, { pool, successResponse, raceId, eventId  }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedWINRaceAllOddsMatrixes: {
      ...(state as IEventTrackRaceState).selectedWINRaceAllOddsMatrixes,
      [pool]: {
        ...(state as IEventTrackRaceState).selectedWINRaceAllOddsMatrixes?.[pool],
        isProcessing: false,
        isSuccess: true,
        response: (() => {
          if (
            (state as IEventTrackRaceState)?.selectedRace?.eventId === eventId
            && (state as IEventTrackRaceState)?.selectedRace?.RaceId === raceId
          ) {
            return successResponse
          }
          else {
            return undefined
          }
        })()
      },
    },
  })),

  // live video
  on(WagerActions.getEventRaceVideoLiveAPISuccess, (state, { successResponse, eventId }) => stateGuard({
    ...state as IEventTrackRaceState,
    entities: (() => {
      // console.log('reducer:getEventRaceVideoLiveAPISuccess:data:,', successResponse )
      if (_.isEmpty(successResponse)) return state?.entities;

      const id = eventId;

      return state?.entities[id]
       ?  {
            ...state?.entities,
            [id]: {
              ...state?.entities[id],
              streamVideo: successResponse?.Link,
              errorMsg: successResponse?.Error
            }
          } as unknown as Dictionary<IGetEventsDetailsResponse>
       : { ...state?.entities }
    })(),
    selectedEventTrack: (() => {
      if (!successResponse) return state?.selectedEventTrack;

      const id = eventId;

      if (id === state?.selectedEventTrack?.id) {
        return  {
          ...state?.selectedEventTrack,
          streamVideo: successResponse?.Link,
          errorMsg: successResponse?.Error
        }
      }

      return state?.selectedEventTrack
    })()
  })),

  // replay video
  on(WagerActions.getEventRaceVideoReplayAPISuccess, (state, { successResponse }) => stateGuard({
    ...state,
    selectedRace: {
      ...state?.selectedRace,
      raceResults: {
        ...state?.selectedRace?.raceResults,
        replayVideo: successResponse?.Link,
        errorMsg: successResponse?.Error
      },
      action: 'getEventRaceVideoReplayAPISuccess'
    } as IRace
  })),

  on(WagerActions.getAllEventsNameAndIdAPISuccess, (state, { successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    allEventsNameAndId: {
      ...(state as IEventTrackRaceState).allEventsNameAndId,
      isProcessing: false,
      isSuccess: true,
      response: successResponse,
    }
  })),

  on(WagerActions.getReplaysAPISuccess, (state, { successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    replays: {
      ...(state as IEventTrackRaceState).replays,
      isProcessing: false,
      isSuccess: true,
      response: successResponse,
    }
  })),

  on(WagerActions.getRunnersAPISuccess, (state, { successResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    runners: successResponse?.Runners
  })),



  /**
   * GET API Response ERROR
   */
  on(WagerActions.getEventsDetailsAPIFailed, (state, { errorResponse }) => stateGuard({
    ...eventTrackRaceAdapter.removeAll({
      ...state,
      ...initialEventTrackRaceState,
      error: errorResponse,
      isRequestingAPI: false
    })
  })),
  on(WagerActions.getRaceResultAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state,
    selectedRace: {
      ...state?.selectedRace,
      raceResults: {
        response: errorResponse,
        isGettingResult: false,
        isSuccessResult: false,
      },
      action: 'getRaceResultAPIFailed'
    } as IRace,
    error: errorResponse,
  })),

  // bet section
  on(WagerActions.getCountersAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    getCounters: {
      ...(state as IEventTrackRaceState).getCounters,
      isProcessing: false,
      isSuccess: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(WagerActions.getBetsAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    getBets: {
      ...(state as IEventTrackRaceState).getBets,
      isProcessing: false,
      isSuccess: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(WagerActions.cancelBetAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    getBets: {
      ...(state as IEventTrackRaceState).getBets,
      isProcessing: false, // default
      isSuccess: false, // default
      response: errorResponse
    },
    error: errorResponse // default
  })),

  on(WagerActions.saveBetAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    saveBet: {
      ...(state as IEventTrackRaceState).saveBet,
      isProcessing: false,
      isSuccess: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(WagerActions.betNowAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    addBetAmount: {
      ...(state as IEventTrackRaceState).addBetAmount,
      isProcessing: false,
      isSuccess: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(WagerActions.getPoolMatrixAPIFailed, (state, { errorResponse, poolId }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRaceMatrix: {
      ...(state as IEventTrackRaceState).selectedRaceMatrix,
      [poolId]: {
        isProcessing: false,
        isSuccess: false,
        response: errorResponse
      },
      error: errorResponse
    }
  })),

  on(WagerActions.getPoolsTotalsAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRacePoolsTotals: {
      ...(state as IEventTrackRaceState).selectedRacePoolsTotals,
      isProcessing: false,
      isSuccess: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(WagerActions.getWillPaysAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRaceWillPays: {
      ...(state as IEventTrackRaceState).selectedRaceWillPays,
      isProcessing: false,
      isSuccess: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(WagerActions.getRaceAllOddsMatrixesAPIFailed, (state, { pool, errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedWINRaceAllOddsMatrixes: {
      ...(state as IEventTrackRaceState).selectedWINRaceAllOddsMatrixes,
      [pool]: {
        ...(state as IEventTrackRaceState).selectedWINRaceAllOddsMatrixes?.[pool],
        isProcessing: false,
        isSuccess: false,
        response: errorResponse
      },
    },
    error: errorResponse
  })),

  on(WagerActions.getAllEventsNameAndIdAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    allEventsNameAndId: {
      ...(state as IEventTrackRaceState).allEventsNameAndId,
      isProcessing: false,
      isSuccess: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(WagerActions.getReplaysAPIFailed, (state, { errorResponse }) => stateGuard({
    ...state as IEventTrackRaceState,
    replays: {
      ...(state as IEventTrackRaceState).replays,
      isProcessing: false,
      isSuccess: false,
      response: errorResponse
    },
    error: errorResponse
  })),



  /**
   * Properties
   */
  // on(WagerActions.setCurrentRace, (state, { id, currentRace }) => stateGuard({
  //   ...state,
  //   entities: (() => {
  //     return state?.entities[id]
  //      ?  {
  //           ...state?.entities,
  //           [id]: {
  //             ...state?.entities[id],
  //             ToteResponse: {
  //               ...state?.entities[id]?.ToteResponse,
  //               EventDetail: {
  //                 ...state?.entities[id]?.ToteResponse?.EventDetail,
  //                 currentRace
  //               }
  //             }
  //           }
  //         } as unknown as Dictionary<IGetEventsDetailsResponse>
  //      : { ...state?.entities }
  //   })()
  // })),

  on(WagerActions.selectedEventTrack, (state, { selectedEventTrack }) => stateGuard({
    ...state,
    selectedEventTrack
  })),

  on(WagerActions.selectedRace, (state, { selectedRace, eventId, runId }) => stateGuard({
    ...state,
    selectedRace: {
      ...selectedRace,
      eventId,
      runId,
      action: 'selectedRace'
    }
  })),

  on(WagerActions.updateRace, (state, { data }) => stateGuard({
    ...state,
    entities: (() => {
      // console.log('reducer:updateRace:data:,', data )
      if (!data) return state?.entities;

      const id = data?.EventId;
      const runId = data?.RunId;
      const newRace = {
        ...data?.Race,
        eventId: id,
        runId
      };

      if (runId !== state?.entities[id]?.ToteResponse?.RunId) return state?.entities;

      return state?.entities[id]
       ?  {
            ...state?.entities,
            [id]: {
              ...state?.entities[id],
              ToteResponse: {
                ...state?.entities[id]?.ToteResponse,
                EventDetail: {
                  ...state?.entities[id]?.ToteResponse?.EventDetail,
                  Races: (() => {
                    const races = state?.entities[id]?.ToteResponse?.EventDetail?.Races || [];
                    return _.map(races, (race) =>  {
                      return (race.RaceId === newRace.RaceId)
                        ? { ...newRace, Current: race?.Current, RaceStatus: race?.RaceStatus }
                        : race
                    })
                  })(),
                  currentRace: (() => {
                    const currentRace = state?.entities[id]?.ToteResponse?.EventDetail?.currentRace;
                    return (currentRace?.RaceId === newRace.RaceId)
                      ? { ...newRace, Current: currentRace?.Current, RaceStatus: currentRace?.RaceStatus }
                      : currentRace
                  })()
                }
              },
            }
          } as unknown as Dictionary<IGetEventsDetailsResponse>
       : { ...state?.entities }
    })(),
    selectedEventTrack: (() => {
      if (!data) return state?.selectedEventTrack;

      const id = data?.EventId;
      const runId = data?.RunId;
      const newRace = {
        ...data?.Race,
        eventId: id,
        runId
      };

      if (runId !== state?.selectedEventTrack?.ToteResponse?.RunId) return state?.selectedEventTrack;

      if (id === state?.selectedEventTrack?.id) {
        return  {
          ...state?.selectedEventTrack,
          ToteResponse: {
            ...state?.selectedEventTrack?.ToteResponse,
            EventDetail: {
              ...state?.selectedEventTrack?.ToteResponse?.EventDetail,
              Races: (() => {
                const races = state?.selectedEventTrack?.ToteResponse?.EventDetail?.Races || [];
                return _.map(races, (race) =>  {
                  return (race.RaceId === newRace.RaceId)
                    ? { ...newRace, Current: race?.Current, RaceStatus: race?.RaceStatus }
                    : race
                })
              })(),
              currentRace: (() => {
                const currentRace = state?.selectedEventTrack?.ToteResponse?.EventDetail?.currentRace;
                return (currentRace?.RaceId === newRace.RaceId)
                  ? { ...newRace, Current: currentRace?.Current, RaceStatus: currentRace?.RaceStatus }
                  : currentRace
              })()
            }
          },
        }
      }

      return state?.selectedEventTrack
    })(),
    selectedRace: (() => {
      if (!data) return state?.selectedRace;

      const id = data?.EventId;
      const runId = data?.RunId;
      const newRace = {
        ...data?.Race,
        eventId: id,
        runId
      };

      if (runId !== state?.selectedRace?.runId) return state?.selectedRace;

      if (id === state?.selectedRace?.eventId) {
        return (state?.selectedRace?.RaceId === newRace?.RaceId)
          ? { ...newRace,  Current: state?.selectedRace?.Current, RaceStatus: state?.selectedRace?.RaceStatus, action: 'updateRace' }
          : state?.selectedRace
      }

      return state?.selectedRace
    })(),
  })),

  on(WagerActions.updateRaceFinal, (state, { data }) => stateGuard({
    ...state,
    entities: (() => {
      if (!data) return state?.entities;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.entities[id]?.ToteResponse?.RunId) return state?.entities;

      return state?.entities[id]
       ?  {
            ...state?.entities,
            [id]: {
              ...state?.entities[id],
              ToteResponse: {
                ...state?.entities[id]?.ToteResponse,
                EventDetail: {
                  ...state?.entities[id]?.ToteResponse?.EventDetail,
                  Races: (() => {
                    const races = state?.entities[id]?.ToteResponse?.EventDetail?.Races || [];
                    return _.map(races, (race) =>  {
                      return (race.RaceId === raceId)
                        ? { ...race, RaceStatus: 3 }
                        : race
                    })
                  })(),
                  currentRace: (() => {
                    const currentRace = state?.entities[id]?.ToteResponse?.EventDetail?.currentRace
                    return (currentRace?.RaceId === raceId)
                      ? { ...currentRace, RaceStatus: 3 }
                      : currentRace
                  })()
                }
              },
            }
          } as unknown as Dictionary<IGetEventsDetailsResponse>
       : { ...state?.entities }
    })(),
    selectedEventTrack: (() => {
      if (!data) return state?.selectedEventTrack;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.selectedEventTrack?.ToteResponse?.RunId) return state?.selectedEventTrack;

      if (id === state?.selectedEventTrack?.id) {
        return  {
          ...state?.selectedEventTrack,
          ToteResponse: {
            ...state?.selectedEventTrack?.ToteResponse,
            EventDetail: {
              ...state?.selectedEventTrack?.ToteResponse?.EventDetail,
              Races: (() => {
                const races = state?.selectedEventTrack?.ToteResponse?.EventDetail?.Races || [];
                return _.map(races, (race) =>  {
                  return (race.RaceId === raceId)
                    ? { ...race, RaceStatus: 3 }
                    : race
                })
              })(),
              currentRace: (() => {
                const currentRace = state?.selectedEventTrack?.ToteResponse?.EventDetail?.currentRace
                return (currentRace?.RaceId === raceId)
                  ? { ...currentRace, RaceStatus: 3 }
                  : currentRace
              })()
            }
          },
        }
      }

      return state?.selectedEventTrack
    })(),
    selectedRace: (() => {
      if (!data) return state?.selectedRace;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.selectedRace?.runId) return state?.selectedRace;

      if (id === state?.selectedRace?.eventId) {
        return (state?.selectedRace?.RaceId === raceId)
          ? { ...state?.selectedRace, RaceStatus: 3, action: 'updateRaceFinal' }
          : state?.selectedRace
      }

      return state?.selectedRace
    })(),
  })),

  on(WagerActions.updateRaceCancelled, (state, { data }) => stateGuard({
    ...state,
    entities: (() => {
      if (!data) return state?.entities;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.entities[id]?.ToteResponse?.RunId) return state?.entities;

      return state?.entities[id]
       ?  {
            ...state?.entities,
            [id]: {
              ...state?.entities[id],
              ToteResponse: {
                ...state?.entities[id]?.ToteResponse,
                EventDetail: {
                  ...state?.entities[id]?.ToteResponse?.EventDetail,
                  Races: (() => {
                    const races = state?.entities[id]?.ToteResponse?.EventDetail?.Races || [];
                    return _.map(races, (race) =>  {
                      return (race.RaceId === raceId)
                        ? { ...race, RaceStatus: 4 }
                        : race
                    })
                  })(),
                  currentRace: (() => {
                    const currentRace = state?.entities[id]?.ToteResponse?.EventDetail?.currentRace
                    return (currentRace?.RaceId === raceId)
                      ? { ...currentRace, RaceStatus: 4 }
                      : currentRace
                  })()
                }
              },
            }
          } as unknown as Dictionary<IGetEventsDetailsResponse>
       : { ...state?.entities }
    })(),
    selectedEventTrack: (() => {
      if (!data) return state?.selectedEventTrack;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.selectedEventTrack?.ToteResponse?.RunId) return state?.selectedEventTrack;

      if (id === state?.selectedEventTrack?.id) {
        return  {
          ...state?.selectedEventTrack,
          ToteResponse: {
            ...state?.selectedEventTrack?.ToteResponse,
            EventDetail: {
              ...state?.selectedEventTrack?.ToteResponse?.EventDetail,
              Races: (() => {
                const races = state?.selectedEventTrack?.ToteResponse?.EventDetail?.Races || [];
                return _.map(races, (race) =>  {
                  return (race.RaceId === raceId)
                    ? { ...race, RaceStatus: 4 }
                    : race
                })
              })(),
              currentRace: (() => {
                const currentRace = state?.selectedEventTrack?.ToteResponse?.EventDetail?.currentRace
                return (currentRace?.RaceId === raceId)
                  ? { ...currentRace, RaceStatus: 4 }
                  : currentRace
              })()
            }
          },
        }
      }

      return state?.selectedEventTrack
    })(),
    selectedRace: (() => {
      if (!data) return state?.selectedRace;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.selectedRace?.runId) return state?.selectedRace;

      if (id === state?.selectedRace?.eventId) {
        return (state?.selectedRace?.RaceId === raceId)
          ? { ...state?.selectedRace, RaceStatus: 4, action: 'updateRaceCancelled' }
          : state?.selectedRace
      }

      return state?.selectedRace
    })(),
  })),

  on(WagerActions.updateRaceClosed, (state, { data }) => stateGuard({
    ...state,
    entities: (() => {
      if (!data) return state?.entities;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.entities[id]?.ToteResponse?.RunId) return state?.entities;

      return state?.entities[id]
       ?  {
            ...state?.entities,
            [id]: {
              ...state?.entities[id],
              ToteResponse: {
                ...state?.entities[id]?.ToteResponse,
                EventDetail: {
                  ...state?.entities[id]?.ToteResponse?.EventDetail,
                  Races: (() => {
                    const races = state?.entities[id]?.ToteResponse?.EventDetail?.Races || [];
                    return _.map(races, (race) =>  {
                      return (race.RaceId === raceId)
                        ? { ...race, RaceStatus: 1 }
                        : race
                    })
                  })(),
                  currentRace: (() => {
                    const currentRace = state?.entities[id]?.ToteResponse?.EventDetail?.currentRace
                    return (currentRace?.RaceId === raceId)
                      ? { ...currentRace, RaceStatus: 1 }
                      : currentRace
                  })()
                }
              },
            }
          } as unknown as Dictionary<IGetEventsDetailsResponse>
       : { ...state?.entities }
    })(),
    selectedEventTrack: (() => {
      if (!data) return state?.selectedEventTrack;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.selectedEventTrack?.ToteResponse?.RunId) return state?.selectedEventTrack;

      if (id === state?.selectedEventTrack?.id) {
        return  {
          ...state?.selectedEventTrack,
          ToteResponse: {
            ...state?.selectedEventTrack?.ToteResponse,
            EventDetail: {
              ...state?.selectedEventTrack?.ToteResponse?.EventDetail,
              Races: (() => {
                const races = state?.selectedEventTrack?.ToteResponse?.EventDetail?.Races || [];
                return _.map(races, (race) =>  {
                  return (race.RaceId === raceId)
                    ? { ...race, RaceStatus: 1 }
                    : race
                })
              })(),
              currentRace: (() => {
                const currentRace = state?.selectedEventTrack?.ToteResponse?.EventDetail?.currentRace
                return (currentRace?.RaceId === raceId)
                  ? { ...currentRace, RaceStatus: 1 }
                  : currentRace
              })()
            }
          },
        }
      }

      return state?.selectedEventTrack
    })(),
    selectedRace: (() => {
      if (!data) return state?.selectedRace;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.selectedRace?.runId) return state?.selectedRace;

      if (id === state?.selectedRace?.eventId) {
        return (state?.selectedRace?.RaceId === raceId)
          ? { ...state?.selectedRace, RaceStatus: 1, action: 'updateRaceClosed' }
          : state?.selectedRace
      }

      return state?.selectedRace
    })(),
  })),

  on(WagerActions.updateRaceOpen, (state, { data }) => stateGuard({
    ...state,
    entities: (() => {
      if (!data) return state?.entities;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      // console.log('reducer:RO:data:', data)
      // console.log('reducer:RO:state?.entities[id]:', state?.entities[id])

      if (runId !== state?.entities[id]?.ToteResponse?.RunId) return state?.entities;

      return state?.entities[id]
       ?  {
            ...state?.entities,
            [id]: {
              ...state?.entities[id],
              ToteResponse: {
                ...state?.entities[id]?.ToteResponse,
                EventDetail: {
                  ...state?.entities[id]?.ToteResponse?.EventDetail,
                  Races: (() => {
                    const races = state?.entities[id]?.ToteResponse?.EventDetail?.Races || [];
                    return _.map(races, (race) =>  {
                      // console.log('reducer:RO:race:', race)
                      return (race.RaceId === raceId)
                        ? { ...race, RaceStatus: 0 }
                        : race
                    })
                  })(),
                  currentRace: (() => {
                    const currentRace = state?.entities[id]?.ToteResponse?.EventDetail?.currentRace
                    return (currentRace?.RaceId === raceId)
                      ? { ...currentRace, RaceStatus: 0 }
                      : currentRace
                  })()
                }
              },
            }
          } as unknown as Dictionary<IGetEventsDetailsResponse>
       : { ...state?.entities }
    })(),
    selectedEventTrack: (() => {
      if (!data) return state?.selectedEventTrack;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.selectedEventTrack?.ToteResponse?.RunId) return state?.selectedEventTrack;

      if (id === state?.selectedEventTrack?.id) {
        return  {
          ...state?.selectedEventTrack,
          ToteResponse: {
            ...state?.selectedEventTrack?.ToteResponse,
            EventDetail: {
              ...state?.selectedEventTrack?.ToteResponse?.EventDetail,
              Races: (() => {
                const races = state?.selectedEventTrack?.ToteResponse?.EventDetail?.Races || [];
                return _.map(races, (race) =>  {
                  return (race.RaceId === raceId)
                    ? { ...race, RaceStatus: 0 }
                    : race
                })
              })(),
              currentRace: (() => {
                const currentRace = state?.selectedEventTrack?.ToteResponse?.EventDetail?.currentRace
                return (currentRace?.RaceId === raceId)
                  ? { ...currentRace, RaceStatus: 0 }
                  : currentRace
              })()
            }
          },
        }
      }

      return state?.selectedEventTrack
    })(),
    selectedRace: (() => {
      if (!data) return state?.selectedRace;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.selectedRace?.runId) return state?.selectedRace;

      if (id === state?.selectedRace?.eventId) {
        return (state?.selectedRace?.RaceId === raceId)
          ? { ...state?.selectedRace, RaceStatus: 0, action: 'updateRaceOpen' }
          : state?.selectedRace
      }

      return state?.selectedRace
    })(),
  })),

  on(WagerActions.updateRacePost, (state, { data }) => stateGuard({
    ...state,
    entities: (() => {
      if (!data) return state?.entities;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.entities[id]?.ToteResponse?.RunId) return state?.entities;

      return state?.entities[id]
       ?  {
            ...state?.entities,
            [id]: {
              ...state?.entities[id],
              ToteResponse: {
                ...state?.entities[id]?.ToteResponse,
                EventDetail: {
                  ...state?.entities[id]?.ToteResponse?.EventDetail,
                  Races: (() => {
                    const races = state?.entities[id]?.ToteResponse?.EventDetail?.Races || [];
                    return _.map(races, (race) =>  {
                      return (race.RaceId === raceId)
                        ? { ...race, RaceStatus: 2 }
                        : race
                    })
                  })(),
                  currentRace: (() => {
                    const currentRace = state?.entities[id]?.ToteResponse?.EventDetail?.currentRace
                    return (currentRace?.RaceId === raceId)
                      ? { ...currentRace, RaceStatus: 2 }
                      : currentRace
                  })()
                }
              },
            }
          } as unknown as Dictionary<IGetEventsDetailsResponse>
       : { ...state?.entities }
    })(),
    selectedEventTrack: (() => {
      if (!data) return state?.selectedEventTrack;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.selectedEventTrack?.ToteResponse?.RunId) return state?.selectedEventTrack;

      if (id === state?.selectedEventTrack?.id) {
        return  {
          ...state?.selectedEventTrack,
          ToteResponse: {
            ...state?.selectedEventTrack?.ToteResponse,
            EventDetail: {
              ...state?.selectedEventTrack?.ToteResponse?.EventDetail,
              Races: (() => {
                const races = state?.selectedEventTrack?.ToteResponse?.EventDetail?.Races || [];
                return _.map(races, (race) =>  {
                  return (race.RaceId === raceId)
                    ? { ...race, RaceStatus: 2 }
                    : race
                })
              })(),
              currentRace: (() => {
                const currentRace = state?.selectedEventTrack?.ToteResponse?.EventDetail?.currentRace
                return (currentRace?.RaceId === raceId)
                  ? { ...currentRace, RaceStatus: 2 }
                  : currentRace
              })()
            }
          },
        }
      }

      return state?.selectedEventTrack
    })(),
    selectedRace: (() => {
      if (!data) return state?.selectedRace;

      const id = data?.EventId;
      const runId = data?.RunId;
      const raceId = data?.RaceId;

      if (runId !== state?.selectedRace?.runId) return state?.selectedRace;

      if (id === state?.selectedRace?.eventId) {
        return (state?.selectedRace?.RaceId === raceId)
          ? { ...state?.selectedRace, RaceStatus: 2, action: 'updateRacePost' }
          : state?.selectedRace
      }

      return state?.selectedRace
    })(),
  })),

  on(WagerActions.updateResultsPersonalStatistics, (state, { countersAPISuccessData }) => stateGuard({
    ...state,
    selectedRace: {
      ...state?.selectedRace,
      raceResults: {
        ...state?.selectedRace?.raceResults,
        personalStatistic: (() => {
          const eventId: string = state?.selectedRace?.raceResults?.ToteResponse?.EventId || '';
          const raceId: string = String(state?.selectedRace?.raceResults?.ToteResponse?.RaceId) || '';

          const ticketCounter: number = countersAPISuccessData?.Counter?.Closed?.[eventId]?.Races?.[raceId]?.Count || 0;
          const ticketAmount: number = countersAPISuccessData?.Counter?.Closed?.[eventId]?.Races?.[raceId]?.Total || 0;
          const earnings: number = countersAPISuccessData?.Counter?.Closed?.[eventId]?.Races?.[raceId]?.Winnings || 0;

          return {
            ticketCounter,
            ticketAmount,
            earnings,
            profitLoss: earnings - ticketAmount
          }
        })()
      },
      action: 'updateResultsPersonalStatistics'
    } as IRace
  })),
  // countersAPISuccessData?.Counter

  // bet section
  on(WagerActions.setTicket, (state, { bet }) => stateGuard({
    ...state as IEventTrackRaceState,
    currentTicket : {
      ...(state as IEventTrackRaceState).currentTicket,
      payload: bet
    },
  })),

  on(WagerActions.setGeoLocation, (state, { location }) => stateGuard({
    ...state as IEventTrackRaceState,
    geolocation: {
      ...(state as IEventTrackRaceState).geolocation,
      ...location
    }
  })),

  on(WagerActions.updateRaceMatrix, (state, { matrix }) => stateGuard({
    ...state,
    selectedRaceMatrix: (() => {

      if (matrix) {
        // console.log('PusherService:PM:reducer:updateRaceMatrix:', matrix);
        return {
          ...state?.selectedRaceMatrix,
          [matrix?.PoolId]: {
            response: {
              ...state?.selectedRaceMatrix?.[matrix?.PoolId]?.response,
              ToteResponse: {
                ...state?.selectedRaceMatrix?.[matrix?.PoolId]?.response?.ToteResponse,
                ...matrix
              }
            }
          }
        }
      }

      return matrix;
    })()
  })),

  on(WagerActions.updateRacePoolsTotals, (state, { poolsTotals }) => stateGuard({
    ...state,
    selectedRacePoolsTotals: poolsTotals
  })),

  on(WagerActions.triggerMatrixModal, (state) => stateGuard({
    ...state,
    triggerMatrixModal: String((new Date()).getMilliseconds() * (Math.floor(Math.random() + 9999)))
  })),

  on(WagerActions.toUpdateGeneralEvents, (state) => stateGuard({
    ...state,
    toUpdateGeneralEvents: String((new Date()).getMilliseconds() * (Math.floor(Math.random() + 9999)))
  })),

  // live video
  on(WagerActions.setEventLiveVideo, (state, { liveEventVideo }) => stateGuard({
    ...state as IEventTrackRaceState,
    getEventLiveVideo: {
      ...(state as IEventTrackRaceState).getEventLiveVideo,
      isProcessing: false,
      isSuccess: true,
      response: liveEventVideo,
    }
  })),

  // replay info
  on(WagerActions.setSelectedReplayInfo, (state, { track, date, breed, race }) => stateGuard({
    ...state as IEventTrackRaceState,
    replayInfo: {
      ...(state as IEventTrackRaceState).replayInfo,
      track,
      date,
      breed,
      race
    }
  })),

  // latest finished event
  on(WagerActions.setLatestFinishedEvent, (state, { latestEvent }) => stateGuard({
    ...state as IEventTrackRaceState,
    latestFinishedEvent: latestEvent
  })),

  // clear odds matrix
  on(WagerActions.clearSelectedRaceMatrix, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRaceMatrix: undefined
  })),

  // clear pool totals matrix
  on(WagerActions.clearSelectedRacePoolsTotals, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRacePoolsTotals: undefined
  })),

  // clear will pays matrix
  on(WagerActions.clearSelectedRaceWillPays, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedRaceWillPays: undefined
  })),

  // clear table and chart matrix
  on(WagerActions.clearRaceAllOddsMatrixes, (state) => stateGuard({
    ...state as IEventTrackRaceState,
    selectedWINRaceAllOddsMatrixes: undefined
  })),

  on(WagerActions.setDefaultEvent, (state, { isSetDefaultEvent }) => stateGuard({
    ...state as IEventTrackRaceState,
    isDefaultEvent: isSetDefaultEvent
  })),





);

export function eventTrackRaceReducer(state: IEventTrackRaceState | undefined, action: Action) {
  return eventTrackRaceReducerFN(state, action);
}





export interface IWagerReducer {
  'eventTrackRace': IEventTrackRaceState
}

export const wagerReducer = {
  'eventTrackRace': eventTrackRaceReducer,
}

